import React, { useEffect, useState, useCallback, useRef } from "react";
import { bannersApi } from "../../services/api";
import { Badge, Button, PageHeader, Modal, Input, Select } from "../../components/ui";
import { showToast } from "../../utils/toast";
import Cropper from "react-easy-crop";
import { getCroppedImg } from "../../utils/cropUtils";
import {
  Trash2,
  Pencil,
  Plus,
  X,
  Scissors,
  FolderOpen,
  ImageIcon,
  MousePointerClick,
  RefreshCw,
  Download,
} from "lucide-react";

const BANNER_W = 354;
const BANNER_H = 145;
const BANNER_ASPECT = BANNER_W / BANNER_H;

// ─── Constants ────────────────────────────────────────────────────────────────
const BANNER_TYPES = [
  "DASHBOARD_HEADER",
  "DASHBOARD_FOOTER",
  "CREDITS_HEADER",
  "CREDITS_FOOTER",
  "WALLET",
];

const ACTION_TYPES = ["IN_APP", "EXTERNAL", "NONE"];

const TYPE_META = {
  DASHBOARD_HEADER: { color: "rgba(15,61,62,0.12)",    border: "rgba(15,61,62,0.25)",    label: "Dashboard Header", dot: "#0F3D3E" },
  DASHBOARD_FOOTER: { color: "rgba(208,179,106,0.15)", border: "rgba(208,179,106,0.3)",  label: "Dashboard Footer", dot: "#a88a45" },
  CREDITS_HEADER:   { color: "rgba(167,139,250,0.15)", border: "rgba(167,139,250,0.3)",  label: "Credits Header",   dot: "#7c3aed" },
  CREDITS_FOOTER:   { color: "rgba(59,130,246,0.12)",  border: "rgba(59,130,246,0.25)",  label: "Credits Footer",   dot: "#2563eb" },
  WALLET:           { color: "rgba(22,163,74,0.12)",   border: "rgba(22,163,74,0.25)",   label: "Wallet",           dot: "#16a34a" },
};

const empty = {
  bannerText:  "",
  image:       "",
  buttonText:  "",
  actionType:  "NONE",
  actionUrl:   "",
  description: "",
  type:        "DASHBOARD_HEADER",
  active:      true,
};

const isValidUrl = (url) => {
  if (!url) return false;
  return url.startsWith('http://') || url.startsWith('https://') || url.startsWith('/');
};

// ─── Inline Toggle ─────────────────────────────────────────────────────────────
function PackToggle({ value, onChange }) {
  return (
    <div onClick={() => onChange(!value)} style={{
      width: 44, height: 24, borderRadius: 12,
      background: value ? "#0F3D3E" : "var(--border)",
      border: `1px solid ${value ? "#0F3D3E" : "var(--border)"}`,
      position: "relative", cursor: "pointer", flexShrink: 0,
      transition: "background 0.2s ease",
    }}>
      <div style={{
        position: "absolute", top: 3,
        left: value ? 22 : 3,
        width: 18, height: 18, borderRadius: "50%",
        background: "#fff",
        boxShadow: "0 1px 4px rgba(0,0,0,0.25)",
        transition: "left 0.2s ease",
      }} />
    </div>
  );
}

// ─── Delete Confirm Modal ──────────────────────────────────────────────────────
function DeleteModal({ banner, onConfirm, onCancel }) {
  if (!banner) return null;
  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 1000,
      background: "rgba(0,0,0,0.45)", backdropFilter: "blur(2px)",
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <div style={{
        background: "var(--bg-card)", borderRadius: 14,
        padding: "28px 32px", width: 380, maxWidth: "90vw",
        border: "1px solid var(--border)", boxShadow: "0 12px 40px rgba(0,0,0,0.25)",
      }}>
        <div style={{ textAlign: "center", marginBottom: 16 }}>
          <div style={{
            width: 52, height: 52, borderRadius: "50%",
            background: "rgba(239,68,68,0.1)",
            display: "flex", alignItems: "center", justifyContent: "center",
            margin: "0 auto 12px",
          }}>
            <Trash2 size={24} color="#ef4444" strokeWidth={2} />
          </div>
          <div style={{ fontSize: 16, fontWeight: 700, color: "var(--text-primary)", marginBottom: 6 }}>
            Delete Banner?
          </div>
          <div style={{ fontSize: 13, color: "var(--text-muted)", lineHeight: 1.6 }}>
            "<strong>{banner.bannerText || "This banner"}</strong>" will be permanently deleted. This action cannot be undone.
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 20 }}>
          <button onClick={onCancel} style={{
            padding: "8px 18px", borderRadius: 8,
            border: "1px solid var(--border)", background: "none",
            cursor: "pointer", fontSize: 13, color: "var(--text-secondary)",
            transition: "all 0.18s",
          }}
            onMouseEnter={e => e.currentTarget.style.borderColor = "#0F3D3E"}
            onMouseLeave={e => e.currentTarget.style.borderColor = "var(--border)"}
          >
            Cancel
          </button>
          <button onClick={onConfirm} style={{
            padding: "8px 18px", borderRadius: 8, border: "none",
            background: "rgba(239,68,68,0.9)", cursor: "pointer",
            fontSize: 13, color: "#fff", fontWeight: 600,
            transition: "background 0.18s",
          }}
            onMouseEnter={e => e.currentTarget.style.background = "#dc2626"}
            onMouseLeave={e => e.currentTarget.style.background = "rgba(239,68,68,0.9)"}
          >
            Yes, Delete
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Type Filter Pill ──────────────────────────────────────────────────────────
function TypePill({ label, active, onClick, dot }) {
  return (
    <button onClick={onClick} style={{
      padding: "5px 14px", borderRadius: 20, fontSize: 12, fontWeight: 500,
      cursor: "pointer", border: "1px solid var(--border)",
      background: active ? "#0F3D3E" : "transparent",
      color: active ? "#fff" : "var(--text-secondary)",
      transition: "all 0.15s", display: "flex", alignItems: "center", gap: 6,
      whiteSpace: "nowrap",
    }}>
      {dot && (
        <span style={{
          width: 7, height: 7, borderRadius: "50%",
          background: active ? "#fff" : dot, flexShrink: 0,
        }} />
      )}
      {label}
    </button>
  );
}

// ─── Image Crop Modal ──────────────────────────────────────────────────────────
function ImageCropModal({ src, onDone, onCancel }) {
  const [crop,        setCrop]        = useState({ x: 0, y: 0 });
  const [zoom,        setZoom]        = useState(1);
  const [croppedArea, setCroppedArea] = useState(null);
  const [applying,    setApplying]    = useState(false);

  const handleCropComplete = useCallback((_, croppedAreaPixels) => {
    setCroppedArea(croppedAreaPixels);
  }, []);

  const handleApply = async () => {
    if (!croppedArea) return;
    setApplying(true);
    try {
      const blob = await getCroppedImg(src, croppedArea, BANNER_W, BANNER_H);
      onDone(blob);
    } catch {
      alert("Crop failed, please try again.");
    }
    setApplying(false);
  };

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 2000,
      background: "rgba(0,0,0,0.75)", backdropFilter: "blur(3px)",
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
    }}>
      <div style={{
        background: "var(--bg-card)", borderRadius: 16, overflow: "hidden",
        width: "min(540px, 95vw)", border: "1px solid var(--border)",
        boxShadow: "0 24px 60px rgba(0,0,0,0.4)",
        display: "flex", flexDirection: "column",
      }}>
        {/* Header */}
        <div style={{
          padding: "14px 18px", borderBottom: "1px solid var(--border)",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text-primary)", display: "flex", alignItems: "center", gap: 7 }}>
              <Scissors size={15} strokeWidth={2} color="#0F3D3E" />
              Crop Banner Image
            </div>
            <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
              Fixed size: {BANNER_W} × {BANNER_H}px · Border radius: 20px
            </div>
          </div>
          <button onClick={onCancel} style={{
            background: "none", border: "none", cursor: "pointer",
            color: "var(--text-muted)", display: "flex", alignItems: "center", justifyContent: "center",
            width: 28, height: 28, borderRadius: 6,
          }}>
            <X size={16} strokeWidth={2} />
          </button>
        </div>

        {/* Crop Area */}
        <div style={{ position: "relative", width: "100%", height: 280, background: "#111" }}>
          <Cropper
            image={src}
            crop={crop}
            zoom={zoom}
            aspect={BANNER_ASPECT}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={handleCropComplete}
            style={{
              containerStyle: { borderRadius: 0 },
              cropAreaStyle: { borderRadius: 20 },
            }}
          />
        </div>

        {/* Zoom Slider */}
        <div style={{ padding: "12px 18px", borderTop: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 11, color: "var(--text-muted)", flexShrink: 0 }}>Zoom</span>
          <input
            type="range" min={1} max={3} step={0.05}
            value={zoom}
            onChange={e => setZoom(Number(e.target.value))}
            style={{ flex: 1, accentColor: "#0F3D3E" }}
          />
          <span style={{ fontSize: 11, color: "var(--text-muted)", flexShrink: 0 }}>{zoom.toFixed(1)}×</span>
        </div>

        {/* Footer */}
        <div style={{
          padding: "12px 18px", borderTop: "1px solid var(--border)",
          display: "flex", gap: 10, justifyContent: "flex-end",
        }}>
          <button onClick={onCancel} style={{
            padding: "8px 18px", borderRadius: 8, border: "1px solid var(--border)",
            background: "none", cursor: "pointer", fontSize: 13,
            color: "var(--text-secondary)", fontFamily: "var(--font-body)",
          }}>Cancel</button>
          <button onClick={handleApply} disabled={applying} style={{
            padding: "8px 18px", borderRadius: 8, border: "none",
            background: "#0F3D3E", color: "#fff", fontSize: 13,
            fontWeight: 600, cursor: applying ? "not-allowed" : "pointer",
            fontFamily: "var(--font-body)", opacity: applying ? 0.7 : 1,
          }}>
            {applying ? "Applying…" : "Apply Crop"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Image Upload Field (with crop) ───────────────────────────────────────────
function ImageUploadField({ imageFile, imageUrl, onFileChange, onUrlChange, onClear }) {
  const fileRef    = useRef(null);
  const [rawSrc,   setRawSrc]   = useState(null);
  const [showCrop, setShowCrop] = useState(false);

  const previewSrc = imageFile
    ? URL.createObjectURL(imageFile)
    : imageUrl || null;

  const handleFileSelect = (file) => {
    const src = URL.createObjectURL(file);
    setRawSrc(src);
    setShowCrop(true);
  };

  const handleCropDone = (blob) => {
    const croppedFile = new File([blob], "banner-cropped.png", { type: "image/png" });
    setShowCrop(false);
    setRawSrc(null);
    onFileChange(croppedFile);
  };

  const handleCropCancel = () => {
    setShowCrop(false);
    setRawSrc(null);
  };

  return (
    <>
      {showCrop && rawSrc && (
        <ImageCropModal src={rawSrc} onDone={handleCropDone} onCancel={handleCropCancel} />
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <label style={{ fontSize: 12, color: "var(--text-secondary)", fontWeight: 500 }}>
          Banner Image
          <span style={{ marginLeft: 8, fontSize: 10, color: "var(--text-muted)", fontWeight: 400 }}>
            (auto-cropped to 708 × 290 px · lossless PNG)
          </span>
        </label>

        {previewSrc && (
          <div style={{ position: "relative", display: "inline-block" }}>
            <img
              src={previewSrc}
              alt="preview"
              style={{
                width: BANNER_W, height: BANNER_H,
                borderRadius: 20, objectFit: "cover", display: "block",
                border: "1px solid var(--border)", maxWidth: "100%",
              }}
              onError={e => e.target.style.display = "none"}
            />
            <button
              type="button" onClick={onClear}
              style={{
                position: "absolute", top: 8, right: 8,
                background: "rgba(0,0,0,0.55)", border: "none", borderRadius: "50%",
                width: 24, height: 24, cursor: "pointer", color: "#fff",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
              title="Remove image"
            >
              <X size={13} strokeWidth={2.5} />
            </button>
          </div>
        )}

        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            style={{
              padding: "7px 14px", borderRadius: 8, fontSize: 12, fontWeight: 600,
              border: "1px dashed var(--border)", background: "var(--bg-input)",
              color: "#0F3D3E", cursor: "pointer",
              display: "flex", alignItems: "center", gap: 6,
              transition: "border-color 0.15s",
            }}
            onMouseEnter={e => e.currentTarget.style.borderColor = "#0F3D3E"}
            onMouseLeave={e => e.currentTarget.style.borderColor = "var(--border)"}
          >
            <FolderOpen size={13} strokeWidth={2} />
            {imageFile ? "Change Image" : "Upload Image"}
          </button>

          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={e => {
              const file = e.target.files?.[0];
              if (file) handleFileSelect(file);
              e.target.value = "";
            }}
          />
        </div>

        <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
          Supported: JPG, PNG, WebP · Exported as lossless PNG at 2× resolution
        </div>
      </div>
    </>
  );
}

// ─── Banner Card ──────────────────────────────────────────────────────────────
function BannerCard({ banner, onEdit, onDelete, onToggle }) {
  const meta = TYPE_META[banner.type] || {
    color: "rgba(15,61,62,0.12)", border: "rgba(15,61,62,0.25)",
    label: banner.type, dot: "#0F3D3E",
  };

  return (
    <div
      style={{
        background: "var(--bg-card)",
        border: "1px solid var(--border)",
        borderRadius: "var(--radius-lg)",
        overflow: "hidden",
        boxShadow: "var(--shadow-sm)",
        display: "flex", flexDirection: "column",
        transition: "box-shadow 0.2s",
      }}
      onMouseEnter={e => e.currentTarget.style.boxShadow = "var(--shadow)"}
      onMouseLeave={e => e.currentTarget.style.boxShadow = "var(--shadow-sm)"}
    >
      {/* Image Preview */}
      {banner.image ? (
        <div style={{ width: "100%", height: 140, overflow: "hidden", background: "var(--bg-input)" }}>
          <img
            src={banner.image} alt={banner.bannerText || "Banner"}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
            onError={e => { e.target.style.display = "none"; }}
          />
        </div>
      ) : (
        <div style={{
          width: "100%", height: 100,
          background: "linear-gradient(135deg, rgba(15,61,62,0.08), rgba(15,61,62,0.03))",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <ImageIcon size={32} color="rgba(15,61,62,0.25)" strokeWidth={1.5} />
        </div>
      )}

      {/* Card Body */}
      <div style={{ padding: "14px 16px", display: "flex", flexDirection: "column", flex: 1 }}>

        {/* Type chip + Active badge */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <span style={{
            fontSize: 10, fontWeight: 700, letterSpacing: "0.5px",
            textTransform: "uppercase", padding: "3px 9px", borderRadius: 20,
            background: meta.color, border: `1px solid ${meta.border}`,
            color: meta.dot,
          }}>
            {meta.label}
          </span>
          <Badge variant={banner.active ? "success" : "danger"}>
            {banner.active ? "Active" : "Inactive"}
          </Badge>
        </div>

        {/* Banner Text */}
        <div style={{
          fontSize: 14, fontWeight: 600, color: "var(--text-primary)",
          marginBottom: 6, lineHeight: 1.4,
          display: "-webkit-box", WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical", overflow: "hidden",
        }}>
          {banner.bannerText || (
            <span style={{ color: "var(--text-muted)", fontStyle: "italic" }}>No text</span>
          )}
        </div>

        {/* Description */}
        {banner.description && (
          <div style={{
            fontSize: 12, color: "var(--text-muted)", marginBottom: 8, lineHeight: 1.5,
            display: "-webkit-box", WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical", overflow: "hidden",
          }}>
            {banner.description}
          </div>
        )}

        {/* Button Text + Action Type */}
        {banner.buttonText && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
            <span style={{
              fontSize: 11, padding: "2px 10px", borderRadius: 20,
              background: "rgba(15,61,62,0.08)", color: "#0F3D3E",
              border: "1px solid rgba(15,61,62,0.15)", fontWeight: 500,
              display: "inline-flex", alignItems: "center", gap: 5,
            }}>
              <MousePointerClick size={11} strokeWidth={2} />
              {banner.buttonText}
            </span>
            {banner.actionType && banner.actionType !== "NONE" && (
              <span style={{ fontSize: 10, color: "var(--text-muted)" }}>
                → {banner.actionType}
              </span>
            )}
          </div>
        )}

        {/* Action URL */}
        {banner.actionUrl && (
          <div style={{
            fontSize: 11, color: "var(--text-muted)", marginBottom: 8,
            fontFamily: "monospace", wordBreak: "break-all",
            background: "var(--bg-input)", borderRadius: 5,
            padding: "3px 8px", border: "1px solid var(--border)",
          }}>
            {banner.actionUrl}
          </div>
        )}

        {/* Actions Row */}
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          marginTop: "auto", paddingTop: 10,
          borderTop: "1px solid var(--border)",
        }}>
          <div style={{ display: "flex", gap: 8 }}>
            <Button size="sm" variant="outline" onClick={() => onEdit(banner)}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                <Pencil size={12} strokeWidth={2} /> Edit
              </span>
            </Button>
            <button
              title="Delete banner"
              onClick={() => onDelete(banner)}
              style={{
                background: "none", border: "none", cursor: "pointer",
                color: "#9ca3af", display: "inline-flex", alignItems: "center",
                padding: 4, borderRadius: 4, transition: "color 0.18s",
              }}
              onMouseEnter={e => e.currentTarget.style.color = "#ef4444"}
              onMouseLeave={e => e.currentTarget.style.color = "#9ca3af"}
            >
              <Trash2 size={16} strokeWidth={2} />
            </button>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 11, color: "var(--text-muted)" }}>
              {banner.active ? "Enabled" : "Disabled"}
            </span>
            <PackToggle value={banner.active} onChange={() => onToggle(banner)} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────
export default function Banners() {
  const [banners,      setBanners]      = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [saving,       setSaving]       = useState(false);
  const [typeFilter,   setTypeFilter]   = useState("");
  const [showModal,    setShowModal]    = useState(false);
  const [editing,      setEditing]      = useState(null);
  const [form,         setForm]         = useState({ ...empty });
  const [imageFile,    setImageFile]    = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    bannersApi.getAll(typeFilter)
      .then(r => setBanners(r.data.data || []))
      .catch(() => showToast("Failed to load banners", "error"))
      .finally(() => setLoading(false));
  }, [typeFilter]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...empty });
    setImageFile(null);
    setShowModal(true);
  };

  const openEdit = (b) => {
    setEditing(b.id);
    setForm({
      bannerText:  b.bannerText  || "",
      image:       b.image       || "",
      buttonText:  b.buttonText  || "",
      actionType:  b.actionType  || "NONE",
      actionUrl:   b.actionUrl   || "",
      description: b.description || "",
      type:        b.type        || "DASHBOARD_HEADER",
      active:      b.active,
    });
    setImageFile(null);
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();

    const buttonText  = form.buttonText.trim();
    const actionUrl   = form.actionUrl.trim();
    const isCtaEnabled = form.actionType !== "NONE";

    if (isCtaEnabled) {
      if (buttonText && !actionUrl) {
        showToast("Action URL is required when Button Text is provided", "error");
        return;
      }
      if (actionUrl && !isValidUrl(actionUrl)) {
        showToast("Please enter a valid Action URL", "error");
        return;
      }
    }

    setSaving(true);

    try {
      const payload = {
        ...form,
        buttonText: isCtaEnabled ? buttonText : "",
        actionUrl:  isCtaEnabled ? actionUrl  : "",
        isActive:   !!form.active,
      };
      delete payload.active;

      if (editing) {
        await bannersApi.update(editing, payload, imageFile);
      } else {
        await bannersApi.create(payload, imageFile);
      }

      await load();
      showToast(editing ? "Banner updated successfully" : "Banner created successfully");
      setShowModal(false);
    } catch (err) {
      showToast(err.response?.data?.message || "Something went wrong", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (b) => {
    try {
      await bannersApi.toggle(b.id, !b.active);
      showToast(`Banner ${!b.active ? "enabled" : "disabled"}`);
      load();
    } catch {
      showToast("Failed to update banner", "error");
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await bannersApi.delete(deleteTarget.id);
      showToast("Banner deleted successfully");
      setDeleteTarget(null);
      load();
    } catch {
      showToast("Failed to delete banner", "error");
    }
  };

  const filtered = typeFilter ? banners.filter(b => b.type === typeFilter) : banners;

  return (
    <div>
      <PageHeader
        title="Banner Controller"
        subtitle="Manage dashboard banners shown in the app"
        action={
          <Button onClick={openCreate}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <Plus size={14} strokeWidth={2.5} /> New Banner
            </span>
          </Button>
        }
      />

      {/* Type Filter Pills */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 20 }}>
        <TypePill label="All" active={typeFilter === ""} onClick={() => setTypeFilter("")} />
        {BANNER_TYPES.map(t => (
          <TypePill
            key={t}
            label={TYPE_META[t]?.label || t}
            dot={TYPE_META[t]?.dot}
            active={typeFilter === t}
            onClick={() => setTypeFilter(t)}
          />
        ))}
      </div>

      {/* Stats Row */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 24 }}>
        {[
          { label: "Total",    value: banners.length,                        color: "#0F3D3E" },
          { label: "Active",   value: banners.filter(b => b.active).length,  color: "#16a34a" },
          { label: "Inactive", value: banners.filter(b => !b.active).length, color: "#dc2626" },
        ].map(({ label, value, color }) => (
          <div key={label} style={{
            background: "var(--bg-card)", border: "1px solid var(--border)",
            borderRadius: "var(--radius)", padding: "10px 20px",
            display: "flex", alignItems: "center", gap: 10,
          }}>
            <span style={{ fontSize: 22, fontWeight: 700, color, fontFamily: "var(--font-display)" }}>
              {value}
            </span>
            <span style={{ fontSize: 12, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.5px" }}>
              {label}
            </span>
          </div>
        ))}
      </div>

      {/* Grid */}
      {loading ? (
        <div style={{ textAlign: "center", padding: 40, color: "var(--text-muted)" }}>Loading…</div>
      ) : filtered.length === 0 ? (
        <div style={{
          textAlign: "center", padding: "48px 24px",
          color: "var(--text-muted)", fontSize: 13,
          border: "1px dashed var(--border)", borderRadius: "var(--radius-lg)",
        }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}>
            <ImageIcon size={32} strokeWidth={1.5} color="var(--text-muted)" />
          </div>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>No banners found</div>
          <div>Click "+ New Banner" to create one.</div>
        </div>
      ) : (
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
          gap: 16,
        }}>
          {filtered.map(b => (
            <BannerCard
              key={b.id}
              banner={b}
              onEdit={openEdit}
              onDelete={setDeleteTarget}
              onToggle={handleToggle}
            />
          ))}
        </div>
      )}

      {/* Create / Edit Modal */}
      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        title={editing ? "Edit Banner" : "New Banner"}
        width={520}
      >
        <form onSubmit={handleSave} style={{ display: "flex", flexDirection: "column", gap: 13 }}>

          <Select
            label="Banner Type"
            value={form.type}
            onChange={e => setForm({ ...form, type: e.target.value })}
            options={BANNER_TYPES.map(t => ({ value: t, label: TYPE_META[t]?.label || t }))}
          />

          <Input
            label="Banner Text"
            value={form.bannerText}
            onChange={e => setForm({ ...form, bannerText: e.target.value })}
            placeholder="e.g. Get 20% off today!"
          />

          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            <label style={{ fontSize: 12, color: "var(--text-secondary)", fontWeight: 500 }}>
              Description
            </label>
            <textarea
              value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })}
              placeholder="Optional description..."
              rows={2}
              style={{
                padding: "9px 12px",
                background: "var(--bg-input)", border: "1px solid var(--border)",
                borderRadius: "var(--radius)", color: "var(--text-primary)", fontSize: 13,
                outline: "none", fontFamily: "var(--font-body)", resize: "vertical",
              }}
              onFocus={e => e.target.style.borderColor = "#0F3D3E"}
              onBlur={e => e.target.style.borderColor = "var(--border)"}
            />
          </div>

          <ImageUploadField
            imageFile={imageFile}
            imageUrl={form.image}
            onFileChange={(file) => {
              setImageFile(file);
              setForm(f => ({ ...f, image: "" }));
            }}
            onUrlChange={(url) => {
              setForm(f => ({ ...f, image: url }));
              setImageFile(null);
            }}
            onClear={() => {
              setImageFile(null);
              setForm(f => ({ ...f, image: "" }));
            }}
          />

          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            <Input
              label="Button Text"
              value={form.buttonText}
              onChange={e => setForm({ ...form, buttonText: e.target.value })}
              placeholder="e.g. Claim Now"
              disabled={!isValidUrl(form.actionUrl)}
            />
            {!isValidUrl(form.actionUrl) && (
              <span style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
                Enter a valid Action URL first to enable this field
              </span>
            )}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Select
              label="Action Type"
              value={form.actionType}
              onChange={e => setForm({ ...form, actionType: e.target.value })}
              options={ACTION_TYPES.map(a => ({ value: a, label: a }))}
            />
            <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              <Input
                label={
                  <span>
                    Action URL{" "}
                    {form.buttonText.trim() && (
                      <span style={{ color: "#dc2626", fontSize: 11 }}>*</span>
                    )}
                  </span>
                }
                value={form.actionUrl}
                onChange={e => setForm({ ...form, actionUrl: e.target.value })}
                placeholder={form.actionType === "EXTERNAL" ? "https://..." : "/route"}
                disabled={form.actionType === "NONE"}
                required={!!form.buttonText.trim()}
              />
              {form.buttonText.trim() && !isValidUrl(form.actionUrl) && (
                <span style={{ fontSize: 11, color: "#dc2626", marginTop: 2 }}>
                  Action URL is required when Button Text is set
                </span>
              )}
            </div>
          </div>

          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "8px 0", borderTop: "1px solid var(--border)",
          }}>
            <span style={{ fontSize: 13, color: "var(--text-secondary)" }}>Active</span>
            <PackToggle value={form.active} onChange={v => setForm({ ...form, active: v })} />
          </div>

          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 4 }}>
            <Button variant="ghost" type="button" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? "Saving…" : editing ? "Update" : "Create"}
            </Button>
          </div>
        </form>
      </Modal>

      <DeleteModal
        banner={deleteTarget}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}